import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Button,
    FormGroup,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    Pagination,
    PaginationItem,
    PaginationLink,
    CardFooter,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import {
    getAllPromotionPlans,
    createPromotionPlan,
    updatePromotionPlan,
    deletePromotionPlan,
} from "../../api/ApiServices";

const styles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
    }
};

const PromotionPlan = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        name: "Basic Plan",
        price: "",
        description: "",
        duration: "",
        unit: "day",
        visibility: "normal"
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [errors, setErrors] = useState({});
    const limit = 10;

    const toggle = () => {
        setModal(!modal);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getAllPromotionPlans(page, limit, searchTerm);
            if (res.data) {
                setPlans(res.data || []);
                setTotal(res.pagination?.total || 0);
            }
        } catch (error) {
            console.error("Error fetching promotion plans:", error);
            Swal.fire("Error", "Failed to load promotion plans", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchTerm]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Please select a valid plan name.";

        if (!formData.price) {
            newErrors.price = "Price is required.";
        } else if (isNaN(formData.price) || Number(formData.price) < 0) {
            newErrors.price = "Price must be a valid positive number.";
        } else if (Number(formData.price) > 10000) {
            newErrors.price = "Price cannot exceed $10,000 (max 5 digits).";
        } else if (!/^\d+(\.\d{1,2})?$/.test(formData.price)) {
            newErrors.price = "Price can have at most 2 decimal places.";
        }

        const maxDuration = formData.unit === "week" ? 52 : formData.unit === "month" ? 12 : formData.unit === "year" ? 10 : 365;
        if (!formData.duration) {
            newErrors.duration = "Duration is required.";
        } else if (isNaN(formData.duration) || Number(formData.duration) <= 0 || !Number.isInteger(Number(formData.duration))) {
            newErrors.duration = "Duration must be a positive integer.";
        } else if (Number(formData.duration) > maxDuration) {
            newErrors.duration = `Duration cannot exceed ${maxDuration} for ${formData.unit || "day"}(s).`;
        }

        if (!formData.unit) newErrors.unit = "Please select a valid unit.";
        if (!formData.visibility) newErrors.visibility = "Please select a visibility option.";
        if (!formData.description || formData.description.trim() === "") {
            newErrors.description = "Description is required.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            const payload = {
                ...formData,
                price: Number(formData.price),
                duration: Number(formData.duration)
            };

            if (editingItem) {
                const res = await updatePromotionPlan(editingItem._id, payload);
                if (res.promotionPlan) {
                    Swal.fire("Success", "Plan updated successfully", "success");
                    fetchData();
                }
            } else {
                const res = await createPromotionPlan(payload);
                if (res.promotionPlan) {
                    Swal.fire("Success", "Plan created successfully", "success");
                    fetchData();
                }
            }
            toggle();
        } catch (error) {
            Swal.fire("Error", error.response?.data?.message || "Failed to save plan", "error");
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setErrors({});
        setFormData({
            name: item.name,
            price: item.price.toString(),
            description: item.description,
            duration: item.duration.toString(),
            unit: item.unit || "day",
            visibility: item.visibility || "normal"
        });
        setModal(true);
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete Promotion Plan?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete"
        });

        if (confirm.isConfirmed) {
            try {
                const res = await deletePromotionPlan(id);
                if (res.message) {
                    Swal.fire("Deleted!", "Plan has been removed.", "success");
                    fetchData();
                }
            } catch (error) {
                Swal.fire("Error", "Failed to delete plan", "error");
            }
        }
    };

    const renderPagination = () => {
        const totalPagesCount = Math.ceil(total / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        for (let i = 1; i <= totalPagesCount; i++) {
            pages.push(
                <PaginationItem key={i} active={page === i}>
                    <PaginationLink onClick={(e) => { e.preventDefault(); setPage(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination className="pagination justify-content-end mb-0">
                <PaginationItem disabled={page === 1}>
                    <PaginationLink onClick={() => setPage(page - 1)} tabIndex="-1"><i className="fas fa-angle-left" /></PaginationLink>
                </PaginationItem>
                {pages}
                <PaginationItem disabled={page === totalPagesCount}>
                    <PaginationLink onClick={() => setPage(page + 1)}><i className="fas fa-angle-right" /></PaginationLink>
                </PaginationItem>
            </Pagination>
        );
    };

    const getVisibilityBadge = (visibility) => {
        switch (visibility) {
            case "pinned":
                return <Badge color="info" pill style={{ background: "#11cdef", color: "#fff", fontWeight: "700" }}>Pinned</Badge>;
            case "highlighted":
                return <Badge color="warning" pill style={{ background: "#fb6340", color: "#fff", fontWeight: "700" }}>Highlighted</Badge>;
            default:
                return <Badge color="secondary" pill style={{ background: "#e9ecef", color: "#525f7f", fontWeight: "700" }}>Normal</Badge>;
        }
    };

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow border-0" style={styles.glassCard}>
                            <CardHeader className="border-0 bg-transparent py-4">
                                <Row className="align-items-center">
                                    <Col md="4">
                                        <h3 className="mb-0 text-dark font-weight-700">
                                            <i className="ni ni-money-coins text-purple mr-2" /> Promotion Plans
                                        </h3>
                                    </Col>
                                    <Col md="8">
                                        <div className="d-flex justify-content-md-end flex-wrap" style={{ gap: "10px" }}>
                                            <Input
                                                placeholder="Search plans..."
                                                type="text"
                                                className="form-control-alternative"
                                                style={{ width: "250px", borderRadius: "8px" }}
                                                value={searchTerm}
                                                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                                            />
                                            <Button color="primary" onClick={() => {
                                                const allPlanNames = ["Basic Plan", "Premium Plan", "Featured Plan"];
                                                const availableNames = allPlanNames.filter(name => !plans.some(p => p.name === name));
                                                if (availableNames.length === 0) {
                                                    Swal.fire("Info", "All available promotion plans have already been created.", "info");
                                                    return;
                                                }
                                                setEditingItem(null);
                                                setErrors({});
                                                setFormData({
                                                    name: availableNames[0],
                                                    price: "",
                                                    description: "",
                                                    duration: "",
                                                    unit: "day",
                                                    visibility: "normal"
                                                });
                                                setModal(true);
                                            }} style={{ borderRadius: "10px" }}>
                                                <i className="fas fa-plus-circle mr-2" /> Add Plan
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody className="p-0">
                                <div className="table-responsive">
                                    <Table className="align-items-center table-flush" hover>
                                        <thead className="thead-light">
                                            <tr>
                                                <th scope="col">Plan Name</th>
                                                <th scope="col">Price</th>
                                                <th scope="col">Duration</th>
                                                <th scope="col">Visibility</th>
                                                <th scope="col">Status</th>
                                                <th scope="col" className="text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr><td colSpan="6" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td></tr>
                                            ) : plans.length === 0 ? (
                                                <tr><td colSpan="6" className="text-center py-5 text-muted">No promotion plans found.</td></tr>
                                            ) : (
                                                plans.map((plan) => (
                                                    <tr key={plan._id}>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span className="font-weight-600 text-dark">{plan.name}</span>
                                                                <small className="text-muted">{plan.description?.substring(0, 50)}...</small>
                                                            </div>
                                                        </td>
                                                        <td><span className="text-success font-weight-700">${plan.price}</span></td>
                                                        <td>
                                                            {plan.duration} {plan.unit || "day"}{plan.duration > 1 ? "s" : ""}
                                                        </td>
                                                        <td>{getVisibilityBadge(plan.visibility)}</td>
                                                        <td>
                                                            <Badge
                                                                color={plan.isActive ? "success" : "danger"}
                                                                pill
                                                                style={{
                                                                    background: plan.isActive ? "#2dce89" : "#f5365c",
                                                                    color: "#fff",
                                                                    fontWeight: "700"
                                                                }}
                                                            >
                                                                {plan.isActive ? "Active" : "Inactive"}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-right">
                                                            <UncontrolledDropdown>
                                                                <DropdownToggle className="btn-icon-only " role="button" size="sm" color="">
                                                                    <i className="fas fa-ellipsis-v" style={{ color: "#6c757d" }} />
                                                                </DropdownToggle>
                                                                <DropdownMenu className="dropdown-menu-arrow" right container="body">
                                                                    <DropdownItem onClick={() => handleEdit(plan)}>Edit</DropdownItem>
                                                                    <DropdownItem divider />
                                                                    <DropdownItem onClick={() => handleDelete(plan._id)} className="text-danger">Delete</DropdownItem>
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                            <CardFooter className="py-4 border-0 bg-transparent d-flex justify-content-between align-items-center">
                                <span className="text-muted small">
                                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
                                </span>
                                {renderPagination()}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={modal} toggle={toggle} centered size="lg">
                <ModalHeader toggle={toggle} className="border-0">
                    {editingItem ? "Edit Promotion Plan" : "Create New Promotion Plan"}
                </ModalHeader>
                <ModalBody className="pt-0">
                    <form onSubmit={handleSubmit} noValidate>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <label className="form-control-label">Plan Name <span className="text-danger">*</span></label>
                                    <Input
                                        type="select"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        style={{ borderRadius: "8px" }}
                                        invalid={!!errors.name}
                                    >
                                        {["Basic Plan", "Premium Plan", "Featured Plan"]
                                            .filter(name => editingItem?.name === name || !plans.some(p => p.name === name))
                                            .map(name => <option key={name} value={name}>{name}</option>)
                                        }
                                    </Input>
                                    {errors.name && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.name}</div>}
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <label className="form-control-label">Price ($) <span className="text-danger">*</span></label>
                                    <Input
                                        type="number"
                                        placeholder="0.00"
                                        min="0"
                                        max="10000"
                                        step="0.01"
                                        value={formData.price}
                                        onChange={(e) => {
                                            let val = e.target.value;
                                            if (val && Number(val) > 10000) val = "10000";
                                            setFormData({ ...formData, price: val });
                                        }}
                                        style={{ borderRadius: "8px" }}
                                        invalid={!!errors.price}
                                    />
                                    {errors.price && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.price}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <label className="form-control-label">Duration <span className="text-danger">*</span></label>
                                    <Input
                                        type="number"
                                        placeholder="e.g. 30"
                                        min="1"
                                        max={formData.unit === "week" ? 52 : formData.unit === "month" ? 12 : formData.unit === "year" ? 10 : 365}
                                        value={formData.duration}
                                        onChange={(e) => {
                                            let val = e.target.value;
                                            const maxDur = formData.unit === "week" ? 52 : formData.unit === "month" ? 12 : formData.unit === "year" ? 10 : 365;
                                            if (val && Number(val) > maxDur) val = String(maxDur);
                                            setFormData({ ...formData, duration: val });
                                        }}
                                        style={{ borderRadius: "8px" }}
                                        invalid={!!errors.duration}
                                    />
                                    {errors.duration && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.duration}</div>}
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <label className="form-control-label">Unit </label>
                                    <Input
                                        type="select"
                                        value={formData.unit}
                                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                                        style={{ borderRadius: "8px" }}
                                        invalid={!!errors.unit}
                                    >
                                        <option value="day">Day(s)</option>
                                        <option value="week">Week(s)</option>
                                        <option value="month">Month(s)</option>
                                        <option value="year">Year(s)</option>
                                    </Input>
                                    {errors.unit && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.unit}</div>}
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <label className="form-control-label">Visibility </label>
                                    <Input
                                        type="select"
                                        value={formData.visibility}
                                        onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                                        style={{ borderRadius: "8px" }}
                                        invalid={!!errors.visibility}
                                    >
                                        <option value="normal">Normal</option>
                                        <option value="highlighted">Highlighted</option>
                                        <option value="pinned">Pinned</option>
                                    </Input>
                                    {errors.visibility && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.visibility}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <FormGroup>
                            <label className="form-control-label">Description <span className="text-danger">*</span></label>
                            <Input
                                type="textarea"
                                rows="3"
                                placeholder="Describe the plan features..."
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                style={{ borderRadius: "8px" }}
                                invalid={!!errors.description}
                            />
                            {errors.description && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.description}</div>}
                        </FormGroup>
                        <div className="text-right">
                            <Button color="secondary" onClick={toggle} style={{ borderRadius: "10px" }}>Cancel</Button>
                            <Button color="primary" type="submit" style={{ borderRadius: "10px", minWidth: "120px" }}>
                                {editingItem ? "Update Plan" : "Create Plan"}
                            </Button>
                        </div>
                    </form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default PromotionPlan;
